import { Gift, DollarSign, AlertTriangle, Clock, XCircle } from 'lucide-react';

interface BillingBadgeProps {
  isBillable: boolean;
  billingType: string | null;
  categoryMismatch: boolean;
  actualCategory: string | null;
  expectedCategory?: string | null;
  cost: number | null;
  status: string;
}

export function BillingBadge({
  isBillable,
  billingType,
  categoryMismatch,
  actualCategory,
  expectedCategory,
  cost,
  status
}: BillingBadgeProps) {
  // Se falhou, mostrar "Sem custo"
  if (status === 'failed') {
    return (
      <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-danger/10 text-danger text-xs font-medium">
        <XCircle className="w-3.5 h-3.5" />
        <span>Sem custo</span>
      </div>
    );
  }

  // Se ainda não chegou no status SENT, não temos dados de billing
  const hasNoBillingData = !['sent', 'delivered', 'read'].includes(status);

  if (hasNoBillingData) {
    return (
      <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-bg-3 text-text-2 text-xs font-medium">
        <Clock className="w-3.5 h-3.5" />
        <span>Aguardando webhook</span>
      </div>
    );
  }

  // Mensagem gratuita (janela de atendimento 24h)
  if (!isBillable) {
    return (
      <div
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-success/10 text-success text-xs font-medium cursor-help"
        title={`Mensagem gratuita - ${billingType === 'free_customer_service' ? 'Dentro da janela de 24h' : billingType || 'Não cobrável'}`}
      >
        <Gift className="w-3.5 h-3.5" />
        <span>Grátis</span>
      </div>
    );
  }

  // Divergência de categoria
  if (categoryMismatch) {
    return (
      <div
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-warning/10 text-warning text-xs font-medium cursor-help"
        title={`Divergência: Esperado ${expectedCategory || 'N/A'}, Recebido ${actualCategory || 'N/A'}`}
      >
        <AlertTriangle className="w-3.5 h-3.5" />
        <span>{cost !== null ? formatCurrency(cost) : 'Cobrável'}</span>
      </div>
    );
  }

  // Mensagem cobrável normal
  return (
    <div
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-info/10 text-accent-1 text-xs font-medium cursor-help"
      title={`Cobrável - Categoria: ${actualCategory || 'N/A'}`}
    >
      <DollarSign className="w-3.5 h-3.5" />
      <span>{cost !== null ? formatCurrency(cost) : 'Cobrável'}</span>
    </div>
  );
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
}
